import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import bankTransferService from '../../../services/bankTransferService';

const BankTransferPanel = ({ payrollRuns }) => {
  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedBank, setSelectedBank] = useState('all');
  const [transferBatches, setTransferBatches] = useState([]);
  const [batchDetails, setBatchDetails] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedBatch, setSelectedBatch] = useState(null);

  useEffect(() => {
    loadTransferBatches();
    loadStatistics();
  }, []);

  useEffect(() => {
    if (selectedBatch) {
      loadBatchDetails(selectedBatch);
    }
  }, [selectedBatch]);

  const loadTransferBatches = async () => {
    try {
      setLoading(true);
      const batches = await bankTransferService?.getTransferBatches();
      setTransferBatches(batches);
    } catch (err) {
      setError(err?.message || 'Failed to load transfer batches');
    } finally {
      setLoading(false);
    }
  };

  const loadBatchDetails = async (batchId) => {
    try {
      const details = await bankTransferService?.getTransferBatchDetails(batchId);
      setBatchDetails(details);
    } catch (err) {
      setError(err?.message || 'Failed to load batch details');
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await bankTransferService?.getTransferStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error('Failed to load statistics:', err);
    }
  };

  const handleCreateBatch = async () => {
    if (!selectedRun) {
      setError('Please select a payroll run');
      return;
    }

    try {
      setGenerating(true);
      setError('');
      setSuccess('');

      const result = await bankTransferService?.createTransferBatchFromPayrollRun(
        selectedRun,
        selectedBank === 'all' ? null : selectedBank,
        'system@example.com'
      );

      if (result?.success) {
        setSuccess(`Transfer batch created successfully! ${result?.totalRecords} records, Total: K ${result?.totalAmount?.toFixed(2)}`);
        await loadTransferBatches();
        await loadStatistics();
        setSelectedBatch(result?.batchId);
      }
    } catch (err) {
      setError(err?.message || 'Failed to create transfer batch');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateFile = async (batchId, format) => {
    try {
      setGenerating(true);
      setError('');

      const result = await bankTransferService?.generateEFTFile(batchId, format);

      if (result?.success) {
        // Create download link
        const blob = new Blob([result?.fileContent], { type: format === 'csv' ? 'text/csv' : 'text/plain' });
        const url = window?.URL?.createObjectURL(blob);
        const link = document?.createElement('a');
        link.href = url;
        link.download = result?.fileName;
        document?.body?.appendChild(link);
        link?.click();
        document?.body?.removeChild(link);
        window?.URL?.revokeObjectURL(url);

        setSuccess(`File generated successfully: ${result?.fileName}`);
        await loadTransferBatches();
      }
    } catch (err) {
      setError(err?.message || 'Failed to generate file');
    } finally {
      setGenerating(false);
    }
  };

  const formatCurrency = (amount) => {
    return `K ${Number(amount || 0)?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-PG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getBankLabel = (bankCode) => {
    const bankLabels = {
      bsp: 'Bank of South Pacific (BSP)',
      kinabank: 'Kinabank',
      credit_bank: 'Credit Bank',
      westpac: 'Westpac',
      anz: 'ANZ',
      other: 'Other Bank'
    };
    return bankLabels?.[bankCode] || bankCode?.toUpperCase();
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      validated: 'bg-blue-100 text-blue-700',
      generated: 'bg-emerald-100 text-emerald-700',
      sent: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-700';
  };

  const selectedRunData = payrollRuns?.find(run => run?.id === selectedRun);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Bank Transfer File Generator</h3>
          <p className="text-sm text-gray-600 mt-1">Generate EFT/ACH files for BSP, Kinabank, and other PNG banks with validation</p>
        </div>
        <Button
          onClick={handleCreateBatch}
          disabled={generating || !selectedRun}
          className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {generating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Creating...
            </>
          ) : (
            <>
              <Icon name="Plus" className="h-5 w-5" />
              Create Transfer Batch
            </>
          )}
        </Button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <Icon name="AlertCircle" className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Icon name="CheckCircle" className="h-5 w-5" />
          <span>{success}</span>
        </div>
      )}

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Batches</p>
                <p className="text-2xl font-bold text-gray-900">{statistics?.totalBatches}</p>
              </div>
              <Icon name="Layers" className="h-8 w-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Draft</p>
                <p className="text-2xl font-bold text-gray-600">{statistics?.draftBatches}</p>
              </div>
              <Icon name="FileText" className="h-8 w-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Generated</p>
                <p className="text-2xl font-bold text-emerald-600">{statistics?.generatedBatches}</p>
              </div>
              <Icon name="CheckCircle" className="h-8 w-8 text-emerald-400" />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Records</p>
                <p className="text-2xl font-bold text-blue-600">{statistics?.totalRecords}</p>
              </div>
              <Icon name="Users" className="h-8 w-8 text-blue-400" />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Amount</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(statistics?.totalAmount)}</p>
              </div>
              <Icon name="DollarSign" className="h-8 w-8 text-emerald-400" />
            </div>
          </div>
        </div>
      )}

      {/* Payroll Run & Bank Selection */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Approved Payroll Run</label>
            <select
              value={selectedRun || ''}
              onChange={(e) => setSelectedRun(e?.target?.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">Choose a payroll run</option>
              {payrollRuns
                ?.filter(run => run?.payrollStatus === 'processed')
                ?.map((run) => (
                  <option key={run?.id} value={run?.id}>
                    {formatDate(run?.payPeriodStart)} - {formatDate(run?.payPeriodEnd)} | 
                    Payment: {formatDate(run?.paymentDate)} | 
                    Total: {formatCurrency(run?.totalNet)}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Bank</label>
            <select
              value={selectedBank}
              onChange={(e) => setSelectedBank(e?.target?.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="all">All Banks</option>
              <option value="bsp">Bank of South Pacific (BSP)</option>
              <option value="kinabank">Kinabank</option>
              <option value="credit_bank">Credit Bank</option>
              <option value="westpac">Westpac</option>
              <option value="anz">ANZ</option>
            </select>
          </div>
        </div>
        {selectedRunData && (
          <div className="grid grid-cols-4 gap-4 text-sm pt-2 border-t border-gray-200">
            <div>
              <span className="text-gray-600">Gross Pay:</span>
              <p className="font-semibold text-gray-900">{formatCurrency(selectedRunData?.totalGross)}</p>
            </div>
            <div>
              <span className="text-gray-600">Net Pay:</span>
              <p className="font-semibold text-emerald-600">{formatCurrency(selectedRunData?.totalNet)}</p>
            </div>
            <div>
              <span className="text-gray-600">SWT:</span>
              <p className="font-semibold text-gray-900">{formatCurrency(selectedRunData?.totalSwt)}</p>
            </div>
            <div>
              <span className="text-gray-600">NASFUND:</span>
              <p className="font-semibold text-gray-900">{formatCurrency(selectedRunData?.totalNasfund)}</p>
            </div>
          </div>
        )}
      </div>

      {/* Transfer Batches List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900">Transfer Batches</h4>
          <p className="text-sm text-gray-600 mt-1">Generated EFT/ACH transfer batches ready for bank submission</p>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            </div>
          ) : transferBatches?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Inbox" className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No transfer batches created yet</p>
              <p className="text-sm text-gray-500 mt-1">Select a payroll run and create your first batch</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Records</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transferBatches?.map((batch) => (
                  <tr key={batch?.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Icon name="FileText" className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">{batch?.batchNumber}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{getBankLabel(batch?.bankName)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(batch?.batchStatus)}`}>
                        {batch?.batchStatus?.replace('_', ' ')?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {batch?.validatedRecords} / {batch?.totalRecords}
                        {batch?.failedRecords > 0 && (
                          <span className="text-red-600 ml-1">({batch?.failedRecords} failed)</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-emerald-600">{formatCurrency(batch?.totalAmountKina)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">{formatDate(batch?.createdAt)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedBatch(batch?.id)}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          View Details
                        </button>
                        {batch?.batchStatus === 'draft' && (
                          <>
                            <button
                              onClick={() => handleGenerateFile(batch?.id, 'ach')}
                              disabled={generating}
                              className="text-emerald-600 hover:text-emerald-700 text-sm font-medium disabled:opacity-50"
                            >
                              Generate ACH
                            </button>
                            <button
                              onClick={() => handleGenerateFile(batch?.id, 'csv')}
                              disabled={generating}
                              className="text-purple-600 hover:text-purple-700 text-sm font-medium disabled:opacity-50"
                            >
                              Generate CSV
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Batch Details Modal */}
      {selectedBatch && batchDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Transfer Batch Details</h3>
                <p className="text-sm text-gray-600 mt-1">{batchDetails?.length} transfer records</p>
              </div>
              <button
                onClick={() => {
                  setSelectedBatch(null);
                  setBatchDetails(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon name="X" className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bank</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Number</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Holder</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {batchDetails?.map((record) => (
                      <tr key={record?.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{record?.employeeName}</div>
                            <div className="text-xs text-gray-500">{record?.employeeNumber}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{getBankLabel(record?.bankName)}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm font-mono text-gray-900">{record?.accountNumber}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{record?.accountHolderName}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm font-semibold text-emerald-600">{formatCurrency(record?.transferAmountKina)}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(record?.recordStatus)}`}>
                            {record?.recordStatus?.replace('_', ' ')?.toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankTransferPanel;